import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Bed, Plus, Edit, Trash2, Users, DollarSign, Hotel } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';

const RoomTypeManagement = () => {
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newRoomType, setNewRoomType] = useState({
    name: '',
    description: '',
    max_occupancy: 2,
    bed_type: '',
    base_rate: '',
    amenities: {}
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchHotels();
  }, []);

  useEffect(() => {
    if (selectedHotel) {
      fetchRoomTypes(selectedHotel);
    }
  }, [selectedHotel]);

  const fetchHotels = async () => {
    try {
      // Fetch brands first
      const brandsResponse = await fetch('/api/tenants/brands', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      
      if (brandsResponse.ok) {
        const brandsData = await brandsResponse.json();
        const allHotels = [];
        
        // Fetch hotels for each brand
        for (const brand of brandsData.brands || []) {
          const hotelsResponse = await fetch(`/api/tenants/brands/${brand.id}/hotels`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
          });
          if (hotelsResponse.ok) {
            const hotelsData = await hotelsResponse.json();
            const hotelsWithBrand = (hotelsData.hotels || []).map(hotel => ({
              ...hotel,
              brand_name: brand.name
            }));
            allHotels.push(...hotelsWithBrand);
          }
        }
        setHotels(allHotels);
        
        // Auto-select first hotel if available
        if (allHotels.length > 0 && !selectedHotel) {
          setSelectedHotel(allHotels[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching hotels:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomTypes = async (hotelId) => {
    try {
      const response = await fetch(`/api/tenants/hotels/${hotelId}/room-types`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoomTypes(data.room_types || []);
      }
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  const createRoomType = async () => {
    if (!selectedHotel || !newRoomType.name.trim()) return;
    
    setCreating(true);
    try {
      const roomTypeData = {
        ...newRoomType,
        base_rate: newRoomType.base_rate ? parseFloat(newRoomType.base_rate) : null
      };
      
      const response = await fetch(`/api/tenants/hotels/${selectedHotel}/room-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
        },
        body: JSON.stringify(roomTypeData),
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoomTypes([...roomTypes, data.room_type]);
        setNewRoomType({
          name: '',
          description: '',
          max_occupancy: 2,
          bed_type: '',
          base_rate: '',
          amenities: {}
        });
        setIsCreateModalOpen(false);
      } else {
        console.error('Failed to create room type');
      }
    } catch (error) {
      console.error('Error creating room type:', error);
    } finally {
      setCreating(false);
    }
  };

  const deleteRoomType = async (roomTypeId) => {
    if (!window.confirm('Are you sure you want to delete this room type?')) return;
    
    try {
      const response = await fetch(`/api/tenants/room-types/${roomTypeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` },
      });
      
      if (response.ok) {
        setRoomTypes(roomTypes.filter(rt => rt.id !== roomTypeId));
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to delete room type');
      }
    } catch (error) {
      console.error('Error deleting room type:', error);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading room types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Room Type Management</h2>
          <p className="text-muted-foreground">
            Define and manage room types for your hotels
          </p>
        </div>
        
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center space-x-2" disabled={!selectedHotel}>
              <Plus className="h-4 w-4" />
              <span>Add Room Type</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Room Type</DialogTitle>
              <DialogDescription>
                Define a new room type for the selected hotel
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="room-name">Room Type Name</Label>
                <Input
                  id="room-name"
                  placeholder="e.g., Deluxe Ocean View"
                  value={newRoomType.name}
                  onChange={(e) => setNewRoomType({...newRoomType, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the room features and amenities"
                  value={newRoomType.description}
                  onChange={(e) => setNewRoomType({...newRoomType, description: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max-occupancy">Max Occupancy</Label>
                  <Select 
                    value={newRoomType.max_occupancy.toString()} 
                    onValueChange={(value) => setNewRoomType({...newRoomType, max_occupancy: parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6].map(num => (
                        <SelectItem key={num} value={num.toString()}>{num} guests</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bed-type">Bed Type</Label>
                  <Select 
                    value={newRoomType.bed_type} 
                    onValueChange={(value) => setNewRoomType({...newRoomType, bed_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select bed type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Bed</SelectItem>
                      <SelectItem value="double">Double Bed</SelectItem>
                      <SelectItem value="queen">Queen Bed</SelectItem>
                      <SelectItem value="king">King Bed</SelectItem>
                      <SelectItem value="twin">Twin Beds</SelectItem>
                      <SelectItem value="bunk">Bunk Beds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="base-rate">Base Rate (USD)</Label>
                <Input
                  id="base-rate"
                  type="number"
                  placeholder="150.00"
                  value={newRoomType.base_rate}
                  onChange={(e) => setNewRoomType({...newRoomType, base_rate: e.target.value})}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={createRoomType} 
                disabled={creating || !newRoomType.name.trim()}
              >
                {creating ? 'Creating...' : 'Create Room Type'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Hotel Selection */}
      {hotels.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Hotel className="h-5 w-5" />
              <span>Select Hotel</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedHotel} onValueChange={setSelectedHotel}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a hotel to manage room types" />
              </SelectTrigger>
              <SelectContent>
                {hotels.map((hotel) => (
                  <SelectItem key={hotel.id} value={hotel.id}>
                    {hotel.name} ({hotel.brand_name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Room Types Grid */}
      {hotels.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Hotel className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No hotels available</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create hotels first before managing room types
            </p>
          </CardContent>
        </Card>
      ) : !selectedHotel ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bed className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Select a hotel</h3>
            <p className="text-muted-foreground text-center">
              Choose a hotel above to view and manage its room types
            </p>
          </CardContent>
        </Card>
      ) : roomTypes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bed className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No room types yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Define your first room type to start managing inventory and pricing
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Add First Room Type</span>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {roomTypes.map((roomType) => (
            <Card key={roomType.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                      <Bed className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{roomType.name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {roomType.description || 'No description provided'}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{roomType.max_occupancy} guests</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{formatCurrency(roomType.base_rate)}</span>
                  </div>
                </div>
                
                {roomType.bed_type && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Bed Type:</span>
                    <Badge variant="secondary">{roomType.bed_type}</Badge>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2">
                  <div className="text-xs text-muted-foreground">
                    ID: {roomType.id.slice(0, 8)}...
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-destructive hover:text-destructive"
                      onClick={() => deleteRoomType(roomType.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default RoomTypeManagement;
